import type { ReactNode } from "react";
import { DropdownMenuItem, cn } from "@wa/ui";

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
  title: string;
}

/**
 * A segmented switch inside the account menu: one thumb that slides between
 * equal slots.
 *
 * Every slot is a kit `DropdownMenuItem` with the `menuitemradio` role, so the
 * menu's arrow keys reach it and a screen reader hears which one is on. A menu
 * only moves focus between its own items and swallows Tab, so plain buttons
 * here could not be reached without a mouse. For use inside a
 * `DropdownMenuContent` only.
 *
 * Picking a slot keeps the menu open (`preventDefault` in `onSelect`): the whole
 * point of a switch is that you see what it did without reopening anything to
 * check.
 *
 * The thumb is one element sliding, not a background moving between slots:
 * that is what makes the movement readable as "the same control changed", and
 * it survives any number of slots because both the width and the travel are
 * derived from the option count.
 */
export function Seg<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly SegOption<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  const index = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <div className="crm-seg" role="group" aria-label={label}>
      <span
        aria-hidden
        className="crm-seg-thumb"
        style={{
          width: `calc((100% - 6px) / ${options.length})`,
          transform: `translateX(${index * 100}%)`,
        }}
      />
      {options.map((o) => {
        const on = o.value === value;
        return (
          <DropdownMenuItem
            key={o.value}
            role="menuitemradio"
            aria-checked={on}
            aria-label={o.title}
            title={o.title}
            // The slots are icons, so name them for the menu's type-to-find.
            textValue={o.title}
            onSelect={(e) => {
              e.preventDefault();
              onChange(o.value);
            }}
            className={cn(
              // `.crm-seg-b` sits in `@layer components`, which the kit item's
              // utilities outrank, so the slot's padding, corners, type size and
              // cursor are restated here. The thumb shows the choice, so focus
              // only lifts the text and the keyboard gets an inset ring.
              "crm-seg-b cursor-pointer justify-center gap-1.5 rounded-full px-3 py-0 text-[11.5px]",
              "focus:bg-transparent focus:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/55",
              on && "is-on",
            )}
          >
            {o.label}
          </DropdownMenuItem>
        );
      })}
    </div>
  );
}
